import { delfinoOptions, delfinoResult } from '../types/delfino';

declare var Delfino: any;
declare var DelfinoConfig: any;

export class DelfinoSdkManager {
  private static _instance: DelfinoSdkManager | null = null;
  static getInstance(): DelfinoSdkManager {
    if (!this._instance) this._instance = new DelfinoSdkManager();
    return this._instance;
  }

  private ready = false;
  private loading: Promise<void> | null = null;

  private injectScript(name: string, src: string): Promise<void> {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      throw new Error('ensureBrowser');
    }
    return new Promise((resolve, reject) => {
      if (!document.getElementById(name)) {
        const scriptLoaded = document.createElement('script');
        scriptLoaded.async = false;
        scriptLoaded.src = src;
        scriptLoaded.id = name;
        document.head.appendChild(scriptLoaded);
        scriptLoaded.addEventListener('load', () => resolve());
        scriptLoaded.addEventListener('error', async (error) => {
          console.log(error);
          reject('JS File Road Fail...');
        });
      } else {
        resolve();
      }
    });
  }

  // JS파일 로드
  async ensureReady(
    lang: 'KOR' | 'ENG' | 'CHN' | 'JPN' | 'VNM',
    timeoutMs = 10_000,
  ): Promise<void> {
    if (this.ready) {
      Delfino.setLang(lang);
      return Promise.resolve();
    }
    if (this.loading) return this.loading;

    this.loading = (async () => {
      if (import.meta.env.VITE_ENVIRONMENT !== 'prod') {
        await this.injectScript(
          'customSystemModeJs',
          '/resource/product/wizvera/delfino/delfino_systemMode.js',
        );
      }
      await this.injectScript(
        'jquery',
        '/resource/product/wizvera/delfino/jquery/jquery-1.11.2.min.js',
      );
      await this.injectScript('config', '/resource/product/wizvera/delfino/delfino_config.js');
      await this.injectScript(
        'internal',
        '/resource/product/wizvera/delfino/delfino_internal.nomin.js',
      );
      await this.injectScript('delfino', '/resource/product/wizvera/delfino/delfino.js');
      await this.injectScript('customJs', '/resource/product/wizvera/delfino/delfino_site.js');

      Delfino.setLang(lang);
      console.log('init done end');
      const start = Date.now();
      while (!(window as any).Delfino) {
        if (Date.now() - start > timeoutMs) throw new Error('SDK 로드 오류');
        await new Promise((r) => setTimeout(r, 25));
      }

      this.ready = true;
      Delfino.setModule('G10');
    })();

    return this.loading;
  }

  /**
   * 인증서 저장위치 설정 변경
   * @param module 인증서 저장위치 설정 변경
   * LOCAL   : 저장위치 브라우저 미노출, 로컬디스크 노출 (cmp 통신)
   * BROWSER : 저장위치 브라우저 노출, 로컬디스크 미노출 (webCmp 통신)
   * OTHER   : G3모듈 설치된 경우 LOCAL(default), 미설치된 경우 BROWSER(default)
   * prepareCertStore : '준비중'으로 노출 할 저장소 설정
   * hardDiskToDefaultStore : 기본 저장소 설정[false(BROWSER), true(LOCAL_DISK)]
   */
  private setModule(module: 'LOCAL' | 'BROWSER' | 'OTHER') {
    if (module === 'LOCAL') {
      DelfinoConfig.cg.prepareCertStore = DelfinoConfig.g4.prepareCertStore + '|BROWSER';
      DelfinoConfig.cg.hardDiskToDefaultStore = true;
    }
    if (module === 'BROWSER') {
      DelfinoConfig.cg.prepareCertStore = DelfinoConfig.g4.prepareCertStore + '|LOCAL_DISK';
      DelfinoConfig.cg.hardDiskToDefaultStore = false;
    }
    if (module == 'OTHER') {
      // 2026-10-07 공동인증서 저장위치가 '브라우저'인 경우 disabled 처리를 위해 로직 수정
      // DelfinoConfig.cg.prepareCertStore = DelfinoConfig.g4.prepareCertStore;
      DelfinoConfig.cg.prepareCertStore = DelfinoConfig.g4.prepareCertStore + '|BROWSER';
      DelfinoConfig.cg.hardDiskToDefaultStore = true;
    }
  }

  /**
   * CERT 로그인-서버에서 인증서만으로 소유자 확인이 가능할 경우에 사용합니다. (사용자의 주민등록번호를 입력받지 않고 nonce값만 사용합니다.)
   * @param complateCB
   */
  certLogin(complateCB: (result?: delfinoResult) => void, delfinoOptions: delfinoOptions) {
    this.setModule('OTHER');
    Delfino.login('login=certLogin', complateCB, delfinoOptions);
  }

  /**
   * VID 로그인-서버에서 인증서만으로 소유자 확인이 불가능할경우 사용합니다. (사용자의 주민등록번호를 추가로 입력받습니다.)
   * @param complateCB
   */
  vidLogin(complateCB: (result?: delfinoResult) => void, delfinoOptions: delfinoOptions) {
    this.setModule('OTHER');
    Delfino.login('login=vidLogin', complateCB, delfinoOptions);
  }

  /**
   * 일반 전자서명 - 사용자에게 전자서명확인창없이 전문형태의 전자서명
   * @param signData
   * @param delfinoOptions
   * @param complateCB
   */
  sign(
    signData: string,
    delfinoOptions: delfinoOptions,
    complateCB: (result?: delfinoResult) => void,
  ) {
    this.setModule('OTHER');
    Delfino.sign(signData, delfinoOptions, complateCB);
  }

  /**
   * 다중전자서명 - 한번의 패스워드 입력으로 여러개의 서명(PKCS7)값을 생성 하고 싶은 경우 사용된다.
   * 서버에서는 여러개를 각각 잘라내 PKCS7값을 검증해야한다.
   * @param signData
   * @param signOptions
   * @param complateCB
   */
  multiSign(
    signData: string,
    signOptions: delfinoOptions,
    complateCB: (result?: delfinoResult) => void,
  ) {
    this.setModule('OTHER');
    Delfino.multiSign(signData, signOptions, complateCB);
  }

  /**
   * 인증서 내보내기
   * @param complateCB
   */
  exportCertificate(complateCB: (result?: delfinoResult) => void) {
    this.setModule('OTHER');
    Delfino.exportCertificate(complateCB);
  }

  /**
   * 인증서 가져오기
   * @param complateCB
   */
  importCertificate(complateCB: (result?: delfinoResult) => void) {
    this.setModule('OTHER');
    Delfino.importCertificate(complateCB);
  }

  /**
   * 인증서 발급 (금결원)
   * @param caName
   * @param refrenceValue
   * @param secretValue
   * @param complateCB
   */
  requestCertificate(
    caName: string,
    referenceValue: string,
    secretValue: string,
    complateCB: (result?: delfinoResult) => void,
    options: delfinoOptions,
  ) {
    this.setModule('LOCAL');
    Delfino.requestCertificate2(caName, referenceValue, secretValue, complateCB, options);
  }

  /**
   * 인증서 발급 (한국정보인증)
   * @param caName
   * @param refrenceValue
   * @param secretValue
   * @param complateCB
   */
  requestCertificate2(
    caName: string,
    referenceValue: string,
    secretValue: string,
    complateCB: (result?: delfinoResult) => void,
    options: delfinoOptions,
  ) {
    this.setModule('LOCAL');
    Delfino.requestCertificate2(caName, referenceValue, secretValue, complateCB, options);
  }

  /**
   * 공동인증서 관리
   */
  manageCertificate() {
    this.setModule('OTHER');
    Delfino.manageCertificate();
  }

  /**
   * 인증서 삭제
   * @param serial
   * @param options
   * @param complateCB
   */
  deleteCertificate(
    serial: number,
    options: delfinoOptions,
    complateCB: (result?: delfinoResult) => void,
  ) {
    this.setModule('OTHER');
    Delfino.deleteCertificate(serial, options, complateCB);
  }

  /**
   * 인증서 갱신
   * @param caName
   * @param complateCB
   * @param options
   */
  updateCertificate2(
    caName: string,
    complateCB: (result?: delfinoResult) => void,
    options: delfinoOptions,
  ) {
    this.setModule('LOCAL');
    Delfino.updateCertificate2(caName, complateCB, options);
  }

  /**
   * 본인인증
   * @param signData
   * @param options
   * @param complateCB
   */
  auth2(signData: string, options: delfinoOptions, complateCB: (result?: delfinoResult) => void) {
    this.setModule('OTHER');
    Delfino.auth2(signData, options, complateCB);
  }

  /**
   * G3 설치여부 확인
   * 모듈을 G3로 변경 후 설치여부 판단 체크 해야함
   * @param complateCB
   */
  isInstall(complateCB: (result: boolean) => void) {
    Delfino.setModule('G3');
    Delfino.isInstall(false, complateCB);
    Delfino.setModule('G10');
  }

  /**
   * 갱신할 인증서 제출
   * 공동인증서 갱신은 인증서 제출한 위치와 갱신 위치가 동일해야 함
   * G3 모듈 미설치 시 인증서 저장위치를 브라우저로   선택 > 브라우저로 끝까지 진행
   *                   인증서 저장위치를 로컬디스크로 선택 > 로컬디스크로 끝까지 진행
   * G3 모듈 설  치 시 인증서 저장위치를 로컬디스크로 끝까지 진행 (저장위치 선택 없음)
   * @param complateCB
   * @param delfinoOptions
   */
  certLogin2(
    complateCB: (result?: delfinoResult) => void,
    certStore: string,
    delfinoOptions: delfinoOptions,
  ) {
    if (certStore == 'LOCAL') {
      // 이 경우는 아마 없을 듯 => 설치 화면으로 이동시키기 때문에
      this.setModule('LOCAL');
    } else if (certStore == 'BROWSER') {
      this.setModule('BROWSER');
    } else {
      this.setModule('OTHER');
    }
    Delfino.login('login=certLogin', complateCB, delfinoOptions);
  }
}
